import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BillingActivityDTO, BillingCustomerDTO, BillingExpenseDTO, BillingMeDTO, BillingOverviewDTO, BillingPaymentDTO,
  BillingPlanDTO, BillingSubscriptionDTO,
  PremiumImageDTO,
} from '@anidex/shared/premium';
import type { Page } from '@anidex/shared/types';
import { api, queryString } from '../api';
import { keys } from './keys';

export function useBillingPlan() {
  return useQuery({ queryKey: ['billing', 'plan'], queryFn: () => api<BillingPlanDTO>('/billing/plan') });
}

export function useBillingMe(enabled: boolean, pollUntil = 0) {
  return useQuery({
    queryKey: ['billing', 'me'], queryFn: () => api<BillingMeDTO>('/billing/me'), enabled,
    refetchOnMount: 'always',
    refetchInterval: (query) => Date.now() < pollUntil && !query.state.data?.premium ? 2000 : false,
  });
}

export function useBillingRedirect(kind: 'checkout' | 'portal') {
  return useMutation({
    mutationFn: () => api<{ url: string }>(`/billing/${kind}`, { body: kind === 'checkout' ? { acceptTerms: true } : {} }),
  });
}

export function useBillingOverview(range: '7' | '30' | '90', enabled: boolean) {
  return useQuery({
    queryKey: ['staff', 'billing', 'overview', range],
    queryFn: () => api<BillingOverviewDTO>(`/staff/billing/overview?range=${range}`), enabled,
  });
}

export function useStaffBillingPlan() {
  return useQuery({ queryKey: ['staff', 'billing', 'plan'], queryFn: () => api<BillingPlanDTO>('/staff/billing/plan') });
}

interface BillingPages {
  subscriptions: Page<BillingSubscriptionDTO>;
  payments: Page<BillingPaymentDTO>;
  customers: Page<BillingCustomerDTO>;
  expenses: Page<BillingExpenseDTO>;
  activity: BillingActivityDTO;
}

export function useBillingList<T extends keyof BillingPages>(kind: T, cursor: string | null = null, enabled = true) {
  return useQuery({
    queryKey: ['staff', 'billing', kind, cursor],
    queryFn: () => api<BillingPages[T]>(`/staff/billing/${kind}${queryString({ cursor })}`),
    enabled,
  });
}

export function useBillingAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { path: string; method?: string; body?: unknown }) =>
      api<{ ok?: boolean; id?: string; status?: string | null }>(`/staff/billing/${input.path}`, { method: input.method, body: input.body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['staff', 'billing'] });
      void client.invalidateQueries({ queryKey: ['billing'] });
      void client.invalidateQueries({ queryKey: keys.session });
      void client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function usePremiumImage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'avatar' | 'banner' | 'card'; characterId?: number; file?: File }) =>
      api<{ url?: string }>(`/me/premium-media/${input.kind}${queryString({ characterId: input.characterId })}`, {
        method: input.file ? 'POST' : 'DELETE', body: input.file,
      }),
    onSuccess: () => {
      for (const key of [keys.session, keys.me, ['premium-images'], ['public_profile'], ['public_collection'], ['collection'], ['character'], ['market'], ['auctions'], ['auction'], ['trades'], ['trade']]) {
        void client.invalidateQueries({ queryKey: key });
      }

    },
  });
}

export function usePremiumImages() {
  return useQuery({ queryKey: ['premium-images'], queryFn: () => api<{ items: PremiumImageDTO[] }>('/me/premium-media') });
}
